import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  CircularProgress,
  TablePagination,
  Chip,
  Link,
  Alert,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import api from '../../utils/api';
import { formatDate } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';

interface Client {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: string;
  createdAt: string;
  lastSessionDate?: string;
  sessionCount?: number;
  isArchived?: boolean;
  counsellorHistory?: Array<{
    counsellorId: string;
    assignedDate: string;
    endDate?: string;
  }>;
}

interface ClientListProps {
  counsellorId?: string; // Optional for backward compatibility
}

const ClientList: React.FC<ClientListProps> = ({ counsellorId }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [counsellorDetails, setCounsellorDetails] = useState<{
    currentClients?: number;
    maxClients?: number;
    feeEarning?: boolean;
    sessionRate?: number;
  }>({});
  const { user } = useAuth();

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        let response;
        
        // If user is admin and counsellorId is provided, fetch specific counsellor's clients
        if (user?.role === 'administrator' && counsellorId) {
          response = await api.get(`/api/counsellors/${counsellorId}/clients`);
          setClients(response.data);
        } else if (user?.role === 'counsellor') {
          // If user is counsellor, fetch their own clients
          response = await api.get('/api/counsellors/clients');
          setClients(response.data.clients || []);
        } else {
          // Invalid scenario
          setClients([]);
        }
        setLoading(false);
      } catch (err: any) {
        setError(err.response?.data?.error || err.response?.data?.message || 'Failed to fetch clients');
        setLoading(false);
      }
    };

    const fetchCounsellorDetails = async () => {
      try {
        let response;
        
        // If user is admin and counsellorId is provided, fetch specific counsellor's profile
        if (user?.role === 'administrator' && counsellorId) {
          response = await api.get(`/api/counsellors/${counsellorId}`);
          setCounsellorDetails({
            currentClients: response.data.currentClients || response.data.currentClientCount || 0,
            maxClients: response.data.maxClients || 0,
            feeEarning: response.data.feeEarning || false,
            sessionRate: response.data.sessionRate || 0
          });
        } else if (user?.role === 'counsellor') {
          // If user is counsellor, fetch their own profile
          response = await api.get('/api/counsellors/profile');
          setCounsellorDetails({
            currentClients: response.data.currentClients || 0,
            maxClients: response.data.maxClients || 0
          });
        }
      } catch (err: any) {
        console.error('Error fetching counsellor details:', err);
      }
    };

    if (user) {
      fetchClients();
      fetchCounsellorDetails();
    }
  }, [counsellorId, user]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMaxClientsChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value)) {
      try {
        // If user is admin and counsellorId is provided, update specific counsellor
        if (user?.role === 'administrator' && counsellorId) {
          await api.patch(`/api/counsellors/${counsellorId}`, { maxClients: value });
        } else if (user?.role === 'counsellor') {
          // If user is counsellor, update their own profile
          await api.put('/api/counsellors/profile', { maxClients: value });
        }
        setCounsellorDetails(prev => ({ ...prev, maxClients: value }));
      } catch (err: any) {
        console.error('Error updating max clients:', err);
      }
    }
  };

  const handleFeeEarningToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    try {
      if (user?.role === 'administrator' && counsellorId) {
        await api.patch(`/api/counsellors/${counsellorId}`, { feeEarning: value });
        setCounsellorDetails(prev => ({ ...prev, feeEarning: value }));
      }
    } catch (err: any) {
      console.error('Error updating fee earning flag:', err);
    }
  };

  const handleSessionRateChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value);
    if (!isNaN(value) && value >= 0) {
      try {
        if (user?.role === 'administrator' && counsellorId) {
          await api.patch(`/api/counsellors/${counsellorId}`, { sessionRate: value });
          setCounsellorDetails(prev => ({ ...prev, sessionRate: value }));
        }
      } catch (err: any) {
        console.error('Error updating session rate:', err);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'not_started':
        return 'info';
      case 'inactive':
        return 'error';
      case 'completed':
        return 'info';
      case 'on hold':
        return 'warning';
      case 'pending_archive':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatStatusLabel = (status: string) => {
    if (status.toLowerCase() === 'pending_archive') {
      return 'Pending Archive';
    }
    if (status.toLowerCase() === 'not_started') {
      return 'Awaiting Appointment';
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Filter clients based on their actual status - all clients returned from the API are active
  // since the backend already filters for non-deleted and non-offboarded clients
  const activeClients = clients.filter(client => 
    !client.isArchived && client.status !== 'completed' && client.status !== 'cancelled'
  );
  const pastClients = clients.filter(client => 
    client.isArchived || client.status === 'completed' || client.status === 'cancelled'
  );

  return (
    <Box sx={{ mt: 2 }}>
      <div className="glass-card p-6 mb-4">
        <Typography variant="h6" className="theme-text-primary" gutterBottom>
          Client Capacity
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Current Clients"
              value={counsellorDetails.currentClients || 0}
              InputProps={{
                readOnly: true,
              }}
              helperText="Number of active clients"
              sx={{
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiInputBase-input': { color: 'rgba(255, 255, 255, 0.9)' },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' }
                },
                '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Maximum Clients"
              type="number"
              value={counsellorDetails.maxClients || 0}
              onChange={handleMaxClientsChange}
              helperText="Maximum number of clients this counsellor can take"
              InputProps={{
                readOnly: user?.role !== 'administrator',
              }}
              sx={{
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiInputBase-input': { color: 'rgba(255, 255, 255, 0.9)' },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' }
                },
                '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' }
              }}
            />
          </Grid>
          {user?.role === 'administrator' && counsellorId && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Session Pay Rate (£)"
                  type="number"
                  value={counsellorDetails.sessionRate || 0}
                  onChange={handleSessionRateChange}
                  helperText="What the organisation pays this counsellor per session - feeds their Tax Digital income"
                  sx={{
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiInputBase-input': { color: 'rgba(255, 255, 255, 0.9)' },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' }
                    },
                    '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={counsellorDetails.feeEarning || false}
                      onChange={handleFeeEarningToggle}
                      sx={{ color: 'rgba(255,255,255,0.6)', '&.Mui-checked': { color: '#4caf50' } }}
                    />
                  }
                  label={
                    <Typography sx={{ color: 'rgba(255,255,255,0.9)' }}>
                      Fee-earning counsellor (session fees recorded as their MTD income)
                    </Typography>
                  }
                />
              </Grid>
            </>
          )}
        </Grid>
      </div>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <div className="glass-card mb-4 rounded-lg overflow-hidden">
        <div className="p-6">
          <Typography variant="h6" className="theme-text-primary" gutterBottom>
            Active Clients ({activeClients.length})
          </Typography>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="glass-card-light">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">Sessions</th>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">Last Session</th>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">Client Since</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {activeClients.length > 0 ? (
                activeClients
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((client) => (
                    <tr key={client._id} className="hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link 
                          component={RouterLink} 
                          to={`/client-details/${client._id}`}
                          sx={{
                            color: 'rgba(255, 255, 255, 0.95)',
                            fontWeight: 600,
                            textDecoration: 'none',
                            '&:hover': {
                              color: 'rgba(100, 181, 246, 1)',
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          {client.firstName} {client.lastName}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap theme-text-secondary">{client.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap theme-text-secondary">{client.phone}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Chip 
                          label={formatStatusLabel(client.status)} 
                          color={getStatusColor(client.status) as any} 
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            '& .MuiChip-label': {
                              px: 1.5
                            }
                          }}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap theme-text-secondary">
                        {client.sessionCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap theme-text-secondary">
                        {client.lastSessionDate ? formatDate(client.lastSessionDate) : 'No sessions yet'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap theme-text-secondary">
                        {client.counsellorHistory && client.counsellorHistory.length > 0 
                          ? formatDate(client.counsellorHistory[client.counsellorHistory.length - 1].assignedDate)
                          : formatDate(client.createdAt)
                        }
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center theme-text-secondary">
                    No active clients found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {activeClients.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={activeClients.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </div>

      <div className="glass-card rounded-lg overflow-hidden">
        <div className="p-6">
          <Typography variant="h6" className="theme-text-primary" gutterBottom>
            Past Clients ({pastClients.length})
          </Typography>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="glass-card-light">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">Sessions</th>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">Last Session</th>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">Client Since</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {pastClients.length > 0 ? (
                pastClients
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((client) => (
                    <tr key={client._id} className="hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link 
                          component={RouterLink} 
                          to={`/client-details/${client._id}`}
                          sx={{
                            color: 'rgba(255, 255, 255, 0.95)',
                            fontWeight: 600,
                            textDecoration: 'none',
                            '&:hover': {
                              color: 'rgba(100, 181, 246, 1)',
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          {client.firstName} {client.lastName}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap theme-text-secondary">{client.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap theme-text-secondary">{client.phone}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Chip 
                          label={formatStatusLabel(client.status)} 
                          color={getStatusColor(client.status) as any} 
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            '& .MuiChip-label': {
                              px: 1.5
                            }
                          }}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap theme-text-secondary">
                        {client.sessionCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap theme-text-secondary">
                        {client.lastSessionDate ? formatDate(client.lastSessionDate) : 'No sessions'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap theme-text-secondary">
                        {client.counsellorHistory && client.counsellorHistory.length > 0 
                          ? formatDate(client.counsellorHistory[client.counsellorHistory.length - 1].assignedDate)
                          : formatDate(client.createdAt)
                        }
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center theme-text-secondary">
                    No past clients found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {pastClients.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={pastClients.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </div>
    </Box>
  );
};

export default ClientList;
